import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, FolderOpen, FileText, Wand2, Clapperboard, Image as ImageIcon,
  Music, Film, Download, Sparkles, ShieldAlert, Bug, Bot,
} from 'lucide-react';
import { Text } from './Text';

/**
 * <AppSidebar> — UI-1 Phase 1 canonical navigation.
 *
 * SAME 12 items rendered on every authenticated route. Eliminates the
 * 5 different sidebar implementations (Dashboard / Recipes / ProjectView /
 * Admin / etc).
 *
 * Active state derives from useLocation() — no parent prop required.
 */

export const SIDEBAR_ITEMS = [
  { id: 'dashboard',      label: 'Dashboard',      icon: Home,         to: '/' },
  { id: 'projects',       label: 'Projects',       icon: FolderOpen,   to: '/projects' },
  { id: 'script',         label: 'Script',         icon: FileText,     to: '/script' },
  { id: 'pre-production', label: 'Pre-Production', icon: Wand2,        to: '/pre-production' },
  { id: 'production',     label: 'Production',     icon: Clapperboard, to: '/production' },
  { id: 'assets',         label: 'Assets',         icon: ImageIcon,    to: '/assets' },
  { id: 'audio',          label: 'Audio',          icon: Music,        to: '/audio' },
  { id: 'post',           label: 'Post-Production', icon: Film,        to: '/post-production' },
  { id: 'exports',        label: 'Exports',        icon: Download,     to: '/exports' },
  { id: 'recipes',        label: 'Recipes',        icon: Sparkles,     to: '/recipes' },
  { id: 'llms',           label: 'LLMs',           icon: Bot,          to: '/llm-studio' },
  { id: 'admin',          label: 'Admin Panel',    icon: ShieldAlert,  to: '/admin' },
  { id: 'debug',          label: 'Debug Panel',    icon: Bug,          to: '/admin/debug' },
];

export function AppSidebar({ collapsed = false, onItemClick }) {
  const loc = useLocation();
  const path = loc?.pathname || '/';

  const isActive = (item) => {
    if (item.to === '/') return path === '/' || path.startsWith('/dashboard');
    return path === item.to || path.startsWith(item.to + '/');
  };

  return (
    <aside
      data-testid="app-sidebar"
      className={`hidden md:flex flex-col shrink-0 bg-[color:var(--color-surface-alt)] border-r border-[color:var(--color-border)] transition-[width] duration-200 ${collapsed ? 'w-[64px]' : 'w-[224px]'}`}
    >
      <div className="px-4 py-5 border-b border-[color:var(--color-border)]">
        <Text variant="mono" tone="secondary" data-testid="app-sidebar-brand">
          {collapsed ? 'EI' : 'EIPI MEDIA'}
        </Text>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {SIDEBAR_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link
              key={item.id}
              to={item.to}
              onClick={onItemClick}
              data-testid={`sidebar-nav-${item.id}`}
              className={`flex items-center gap-3 mx-2 my-0.5 px-3 py-2 rounded-[var(--radius-md)] transition-colors duration-150 ${
                active
                  ? 'bg-[color:var(--color-primary-soft)] text-[color:var(--color-primary)]'
                  : 'text-[color:var(--color-text-secondary)] hover:bg-white/[0.04] hover:text-[color:var(--color-text-primary)]'
              }`}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && (
                <span className="text-[14px] font-medium truncate">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

export default AppSidebar;
